import { useState, useEffect, useRef } from "react";
import {
  useClick,
  useInteractions,
  useFloating,
  useDismiss,
  FloatingPortal,
  FloatingArrow,
  arrow,
  offset,
  flip,
  shift,
} from "@floating-ui/react";

export function Dropdown({
  button,
  children,
  hideOnClick = [],
  isOpened: externalIsOpened = false,
  setIsOpened: externalSetIsOpened,
  portalId, // https://floating-ui.com/docs/floatingportal#id
}: {
  button: React.ReactNode;
  children: React.ReactNode;
  hideOnClick?: string[];
  isOpened?: boolean;
  setIsOpened?: (isOpened: boolean) => void;
  portalId?: string;
}) {
  const [isOpen, setIsOpen] = useState(externalIsOpened);
  const arrowRef = useRef(null);

  useEffect(() => {
    if (!!externalSetIsOpened) {
      setIsOpen(externalIsOpened);
    }
  }, [externalIsOpened, externalSetIsOpened]);

  // Close dropdown when drawer opens or when Turbo navigates
  useEffect(() => {
    const closeDropdown = () => {
      if (externalSetIsOpened) {
        externalSetIsOpened(false);
      } else {
        setIsOpen(false);
      }
    };

    window.addEventListener("drawer:opened", closeDropdown);
    document.addEventListener("turbo:before-visit", closeDropdown);
    return () => {
      window.removeEventListener("drawer:opened", closeDropdown);
      document.removeEventListener("turbo:before-visit", closeDropdown);
    };
  }, [externalSetIsOpened]);

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (hideOnClick.length >= 1) {
      const matches = hideOnClick.find((selector) =>
        (e.target as HTMLElement).closest(selector)
      );

      if (matches) {
        if (externalSetIsOpened) {
          externalSetIsOpened(false);
        } else {
          setIsOpen(false);
        }
      }
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: externalSetIsOpened || setIsOpen,
    placement: "bottom",
    middleware: [
      offset(10),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const click = useClick(context, { toggle: true });
  const dismiss = useDismiss(context, {
    outsidePress: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const arrowClasses =
    "fill-white z-[100] [&>path:first-of-type]:stroke-slate-200 z-[2]";

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="cursor-pointer truncate"
      >
        {button}
      </div>
      {isOpen && (
        <FloatingPortal id={portalId}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[10]"
          >
            <div
              onClick={handleContentClick}
              className="bg-white rounded-lg ring-1 ring-solid ring-slate-200 shadow-lg relative z-[1] overflow-hidden"
            >
              {children}
            </div>
            <FloatingArrow
              width={16}
              height={10}
              ref={arrowRef}
              context={context}
              className={arrowClasses}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
